import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Alert, Paper, MenuItem,
  Select, FormControl, InputLabel, Switch, FormControlLabel,
  CircularProgress, IconButton, InputAdornment, Chip, Divider,
  RadioGroup, Radio, FormLabel
} from '@mui/material';
import {
  SmartToy, Save, Visibility, VisibilityOff, CheckCircle,
  Science, AutoAwesome
} from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PROVEEDORES = {
  openai:    { label: 'OpenAI (ChatGPT)', modelos: ['gpt-4o-mini', 'gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo'], keyUrl: 'https://platform.openai.com/api-keys', keyHelp: 'Obtén tu key en platform.openai.com/api-keys' },
  anthropic: { label: 'Anthropic (Claude)', modelos: ['claude-3-5-haiku-20241022', 'claude-3-5-sonnet-20241022', 'claude-3-opus-20240229'], keyUrl: 'https://console.anthropic.com/settings/keys', keyHelp: 'Obtén tu key en console.anthropic.com' },
  gemini:    { label: 'Google Gemini', modelos: ['gemini-3.6-flash', 'gemini-3.6-pro', 'gemini-2.5-flash'], keyUrl: 'https://aistudio.google.com/apikey', keyHelp: 'Obtén tu key en aistudio.google.com/apikey' },
  deepseek:  { label: 'DeepSeek', modelos: ['deepseek-chat', 'deepseek-reasoner'], keyUrl: 'https://platform.deepseek.com/api_keys', keyHelp: 'Obtén tu key en platform.deepseek.com' },
};

export default function AsistenteIA() {
  const [config, setConfig] = useState({
    proveedor: 'openai',
    api_key: '',
    modelo: '',
    prompt_sistema: 'Eres el asistente virtual de atención al cliente. Responde de forma amable, breve y profesional.',
    contexto_negocio: '',
    modo: 'DESACTIVADO',
    activo: false,
  });
  const [notif, setNotif] = useState({
    notificar_email: true,
    notificar_app: true,
    email_destino: '',
    eventos: { lead_nuevo: true, intervencion_humana: true },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showKey, setShowKey] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [resIA, resNotif] = await Promise.all([
        axios.get(`${API}/api/ia/config`, { headers: headers() }),
        axios.get(`${API}/api/notificaciones/config`, { headers: headers() }).catch(() => null),
      ]);
      if (resIA.data.data) setConfig(prev => ({ ...prev, ...resIA.data.data }));
      if (resNotif?.data?.data) setNotif(prev => ({ ...prev, ...resNotif.data.data, eventos: { ...prev.eventos, ...(resNotif.data.data.eventos || {}) } }));
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando configuración');
    } finally { setLoading(false); }
  }

  async function handleSave() {
    setSaving(true); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/api/ia/config`, config, { headers: headers() });
      // Guardar también la configuración de notificaciones
      await axios.post(`${API}/api/notificaciones/config`, notif, { headers: headers() }).catch(() => {});
      setSuccess('Configuración guardada correctamente');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando');
    } finally { setSaving(false); }
  }

  async function handleTest() {
    setTesting(true); setTestResult(''); setError('');
    try {
      const res = await axios.post(`${API}/api/ia/probar`, config, { headers: headers() });
      setTestResult(res.data.respuesta);
    } catch (e) {
      setError(e.response?.data?.error || 'Error probando la conexión');
    } finally { setTesting(false); }
  }

  const provInfo = PROVEEDORES[config.proveedor] || PROVEEDORES.openai;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 800 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ background: 'linear-gradient(135deg,#7c4dff,#005eff)', borderRadius: 2, p: 1, display: 'flex' }}>
          <SmartToy sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>Asistente IA</Typography>
          <Typography variant="body2" color="text.secondary">Respuestas automáticas con inteligencia artificial en tu chat y WhatsApp</Typography>
        </Box>
        {config.activo && config.modo === 'AUTOMATICO' && (
          <Chip label="Activo" color="success" size="small" icon={<CheckCircle />} sx={{ ml: 'auto' }} />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Activación y modo */}
        <FormControlLabel
          control={<Switch checked={config.activo} onChange={e => setConfig(p => ({ ...p, activo: e.target.checked }))} />}
          label={<Box><Typography variant="body2" fontWeight={600}>Asistente IA habilitado</Typography><Typography variant="caption" color="text.secondary">Activa la IA para tus conversaciones</Typography></Box>}
        />

        <Box sx={{ mt: 2, mb: 1 }}>
          <FormLabel><Typography variant="body2" fontWeight={600}>Modo de operación</Typography></FormLabel>
          <RadioGroup value={config.modo} onChange={e => setConfig(p => ({ ...p, modo: e.target.value }))}>
            <FormControlLabel value="AUTOMATICO" control={<Radio size="small" />}
              label={<Box><Typography variant="body2">Respuesta automática</Typography><Typography variant="caption" color="text.secondary">La IA responde sola a los clientes (hasta que un agente tome la conversación)</Typography></Box>} />
            <FormControlLabel value="SUGERENCIA" control={<Radio size="small" />}
              label={<Box><Typography variant="body2">Solo sugerencias</Typography><Typography variant="caption" color="text.secondary">La IA sugiere respuestas pero el agente decide (próximamente)</Typography></Box>} />
            <FormControlLabel value="DESACTIVADO" control={<Radio size="small" />}
              label={<Typography variant="body2">Desactivado</Typography>} />
          </RadioGroup>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Proveedor de IA</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <InputLabel>Proveedor</InputLabel>
            <Select value={config.proveedor} label="Proveedor"
              onChange={e => setConfig(p => ({ ...p, proveedor: e.target.value, modelo: '' }))}>
              {Object.entries(PROVEEDORES).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <InputLabel>Modelo</InputLabel>
            <Select value={config.modelo || provInfo.modelos[0]} label="Modelo"
              onChange={e => setConfig(p => ({ ...p, modelo: e.target.value }))}>
              {provInfo.modelos.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <TextField
          label="API Key"
          type={showKey ? 'text' : 'password'}
          value={config.api_key}
          onChange={e => setConfig(p => ({ ...p, api_key: e.target.value }))}
          fullWidth
          helperText={provInfo.keyHelp}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowKey(p => !p)}>{showKey ? <VisibilityOff /> : <Visibility />}</IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Personalidad y contexto</Typography>
        <TextField
          label="Instrucciones del asistente (prompt)"
          multiline rows={3}
          value={config.prompt_sistema}
          onChange={e => setConfig(p => ({ ...p, prompt_sistema: e.target.value }))}
          fullWidth sx={{ mb: 2 }}
          helperText="Define cómo debe comportarse y responder la IA"
        />
        <TextField
          label="Información del negocio"
          multiline rows={5}
          value={config.contexto_negocio}
          onChange={e => setConfig(p => ({ ...p, contexto_negocio: e.target.value }))}
          fullWidth
          placeholder={"Ejemplo:\nHorario: Lunes a viernes 8am-6pm\nProductos: exámenes médicos, laboratorio clínico\nDirección: Av. América y Voz Andes\nFormas de pago: efectivo, transferencia, tarjeta"}
          helperText="La IA usará esta información para responder preguntas sobre tu negocio"
        />
      </Paper>

      {/* Prueba */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9ff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: testResult ? 2 : 0 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>Probar el asistente</Typography>
            <Typography variant="caption" color="text.secondary">Envía un "Hola" de prueba a la IA con la config actual</Typography>
          </Box>
          <Button variant="outlined" startIcon={testing ? <CircularProgress size={16} /> : <Science />}
            onClick={handleTest} disabled={testing || !config.api_key}>
            {testing ? 'Probando...' : 'Probar'}
          </Button>
        </Box>
        {testResult && (
          <Alert severity="success" icon={<AutoAwesome />} sx={{ mt: 1 }}>
            <Typography variant="caption" fontWeight={700} display="block">Respuesta de la IA:</Typography>
            {testResult}
          </Alert>
        )}
      </Paper>

      {/* Notificaciones */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>Notificaciones</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Recibe un aviso cuando entra un lead nuevo o cuando la IA necesita que un agente intervenga.
        </Typography>

        <FormControlLabel
          control={<Switch checked={notif.notificar_app !== false} onChange={e => setNotif(p => ({ ...p, notificar_app: e.target.checked }))} />}
          label={<Box><Typography variant="body2" fontWeight={600}>Notificar en la app</Typography><Typography variant="caption" color="text.secondary">Aparece en la campana del panel en tiempo real</Typography></Box>}
        />
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={<Switch checked={notif.notificar_email !== false} onChange={e => setNotif(p => ({ ...p, notificar_email: e.target.checked }))} />}
            label={<Box><Typography variant="body2" fontWeight={600}>Notificar por correo</Typography><Typography variant="caption" color="text.secondary">Requiere tener el correo (SMTP) configurado</Typography></Box>}
          />
        </Box>

        {notif.notificar_email !== false && (
          <TextField
            label="Correo de destino (opcional)" type="email" size="small" fullWidth sx={{ mt: 2 }}
            value={notif.email_destino || ''}
            onChange={e => setNotif(p => ({ ...p, email_destino: e.target.value }))}
            placeholder="avisos@tuempresa.com"
            helperText="Si lo dejas vacío, se usa el correo del administrador de la cuenta"
          />
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>¿Qué eventos notificar?</Typography>
        <FormControlLabel
          control={<Switch size="small" checked={notif.eventos?.lead_nuevo !== false} onChange={e => setNotif(p => ({ ...p, eventos: { ...p.eventos, lead_nuevo: e.target.checked } }))} />}
          label={<Typography variant="body2">🎯 Nuevo lead desde el chat web</Typography>}
        />
        <Box>
          <FormControlLabel
            control={<Switch size="small" checked={notif.eventos?.intervencion_humana !== false} onChange={e => setNotif(p => ({ ...p, eventos: { ...p.eventos, intervencion_humana: e.target.checked } }))} />}
            label={<Typography variant="body2">🙋 La IA necesita intervención humana</Typography>}
          />
        </Box>
      </Paper>

      <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
        onClick={handleSave} disabled={saving} sx={{ px: 4 }}>
        {saving ? 'Guardando...' : 'Guardar Configuración'}
      </Button>
    </Box>
  );
}
